import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { supervisorService } from '../services/supervisorService';

/**
 * SupervisorDropdown - A reusable dropdown component to select supervisors
 *
 * @param {Object} props - Component props
 * @param {string} props.name - Input field name
 * @param {string} props.value - Selected supervisor value
 * @param {function} props.onChange - Function to call when selection changes
 * @param {boolean} props.required - Whether the field is required
 * @param {string} props.label - Label text for the dropdown
 * @param {boolean} props.isInvalid - Whether the field is invalid (for validation)
 * @param {string} props.invalidFeedback - Feedback message for invalid state
 */
const SupervisorDropdown = ({
                                name = 'supervisor',
                                value = '',
                                onChange,
                                required = false,
                                label = 'Supervisor',
                                isInvalid = false,
                                invalidFeedback = 'Please select a supervisor',
                                placeholder = 'Select a supervisor'
                            }) => {
    const [supervisors, setSupervisors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch supervisors for dropdown
    useEffect(() => {
        const fetchSupervisors = async () => {
            try {
                setLoading(true);
                const data = await supervisorService.getSupervisorsForDropdown();
                setSupervisors(data);
                setError(null);
            } catch (err) {
                console.error('Error fetching supervisors for dropdown', err);
                setError('Could not load supervisors');
            } finally {
                setLoading(false);
            }
        };

        fetchSupervisors();
    }, []);

    // Handle change and call parent onChange
    const handleChange = (e) => {
        if (onChange) {
            onChange(e);
        }
    };

    return (
        <Form.Group className="mb-3">
            {label && <Form.Label>{label}</Form.Label>}
            <Form.Select
                name={name}
                value={value}
                onChange={handleChange}
                required={required}
                isInvalid={isInvalid}
                disabled={loading}
            >
                <option value="">{loading ? 'Loading supervisors...' : placeholder}</option>
                {supervisors.map((supervisor) => (
                    <option key={supervisor.id} value={supervisor.id}>
                        {supervisor.name}
                    </option>
                ))}
            </Form.Select>
            {error && <div className="text-danger small mt-1">{error}</div>}
            {isInvalid && <Form.Control.Feedback type="invalid">{invalidFeedback}</Form.Control.Feedback>}
        </Form.Group>
    );
};

SupervisorDropdown.propTypes = {
    name: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    required: PropTypes.bool,
    label: PropTypes.string,
    isInvalid: PropTypes.bool,
    invalidFeedback: PropTypes.string,
    placeholder: PropTypes.string
};

export default SupervisorDropdown;